/**
 * serviceLayerClient.auth — Supabase JWT read + staleness + the
 * `service-layer:unauthorized` dispatch.
 *
 * Extracted from serviceLayerClient.ts (LOC ceiling, behaviour-preserving).
 * This is the Bearer-JWT fallback path for browsers that block / partition the
 * HttpOnly cookies set by `/v1/auth/cookie-handoff` (iOS Safari ITP). The
 * cookie path stays primary in serviceFetch; these helpers only mint the
 * `Authorization: Bearer <jwt>` fallback and decide when a token is stale.
 */

// SL Supabase client storageKey (must stay in sync with
// packages/service-layer/src/supabaseClient.ts). Read directly here so SL
// requests can carry `Authorization: Bearer ${access_token}` alongside the
// HttpOnly-cookie path — needed for browsers that block / partition the
// cookies (iOS Safari ITP). See `getSupabaseAccessTokenFromStorage` below.
const SL_AUTH_STORAGE_KEY = 'sb-clanda-sl';

/**
 * Pull the Supabase access_token from localStorage (written by the SL client
 * via `setSession` after Azure callback). Returns null when:
 *   - localStorage is unavailable (SSR, sandboxed iframe, blocked storage)
 *   - the key is absent (logged out, or setSession never ran)
 *   - the JSON is malformed
 *
 * Used to construct the `Authorization: Bearer <jwt>` header — see
 * `serviceFetch` for the wiring. The cookie path remains primary; this
 * Bearer header is the iOS-Safari-ITP fallback for SL endpoints, per
 * BUG-SL-SCOUT-REP-CANNOT-READ-OWN-MISSIONS-2026-05-26.
 */
export function getSupabaseAccessTokenFromStorage(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(SL_AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { access_token?: string };
    return parsed?.access_token ?? null;
  } catch {
    return null;
  }
}

// Seconds before a JWT's `exp` at which we treat it as effectively expired and
// proactively recycle it, rather than racing the clock on a request that would
// otherwise 401. Supabase access tokens are short-lived (~1h); 60s is a safe
// margin that never triggers on a freshly-minted token.
const JWT_EXPIRY_SKEW_SECONDS = 60;

/**
 * Decode the `exp` (seconds since epoch) claim from a JWT's payload segment.
 *
 * The token is `header.payload.signature`; the payload is base64url-encoded
 * JSON. Returns the numeric `exp`, or `null` for any malformed / missing /
 * non-numeric value — callers MUST treat `null` as "expired" (fail closed,
 * never assume a bad token is fresh).
 */
function decodeJwtExpSeconds(token: string): number | null {
  const segments = token.split('.');
  if (segments.length !== 3) return null;
  const payloadSegment = segments[1];
  if (!payloadSegment) return null;
  try {
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    // atob is a global in all supported runtimes (browsers + Node >=16 + jsdom). The
    // previous Buffer fallback was dead code that leaked an @types/node dependency into
    // browser-only consumers (e.g. command-palette tsc -b). Fail closed if it were ever
    // absent — matching this function's null = "expired" contract above.
    if (typeof atob !== 'function') return null;
    const json = atob(base64);
    const payload = JSON.parse(json) as { exp?: unknown };
    if (typeof payload.exp === 'number' && Number.isFinite(payload.exp)) {
      return payload.exp;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Whether a JWT is expired or within `JWT_EXPIRY_SKEW_SECONDS` of expiry.
 * Malformed / undecodable tokens are reported stale (fail closed).
 */
export function isAccessTokenStale(token: string): boolean {
  const exp = decodeJwtExpSeconds(token);
  if (exp === null) return true;
  const nowSeconds = Date.now() / 1000;
  return exp - JWT_EXPIRY_SKEW_SECONDS <= nowSeconds;
}

/**
 * Dispatch the global `service-layer:unauthorized` event so the session
 * overlay (SessionExpiredOverlay, mounted in Providers) can prompt
 * re-authentication. SSR-safe and idempotent — the overlay dedupes.
 */
export function dispatchUnauthorized(detail: Record<string, unknown>): void {
  if (typeof window === 'undefined' || typeof CustomEvent === 'undefined') return;
  window.dispatchEvent(new CustomEvent('service-layer:unauthorized', { detail }));
}
